import { ChevronRight } from 'lucide-react';
import { type ReactNode, useEffect } from 'react';
import { Link } from 'react-router';

export type Breadcrumb = { label: string; to?: string };

export type PageHeaderProps = {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned buttons. The primary action goes last. */
  actions?: ReactNode;
  breadcrumbs?: readonly Breadcrumb[];
  /** Browser tab title; defaults to `title` when it is a string. */
  documentTitle?: string;
  /** Content between title and actions, e.g. badges next to a lead name. */
  meta?: ReactNode;
};

const APP_NAME = 'Hearth CRM';

export function PageHeader({ title, description, actions, breadcrumbs, documentTitle, meta }: PageHeaderProps) {
  const tabTitle = documentTitle ?? (typeof title === 'string' ? title : undefined);
  useEffect(() => {
    document.title = tabTitle ? `${tabTitle} · ${APP_NAME}` : APP_NAME;
  }, [tabTitle]);

  return (
    <header className="flex flex-col gap-1.5">
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
            {breadcrumbs.map((crumb, index) => (
              <li key={`${crumb.label}-${index}`} className="flex items-center gap-1">
                {index > 0 && <ChevronRight size={12} aria-hidden className="text-slate-300" />}
                {crumb.to ? (
                  <Link to={crumb.to} className="rounded hover:text-slate-900 hover:underline">
                    {crumb.label}
                  </Link>
                ) : (
                  <span aria-current={index === breadcrumbs.length - 1 ? 'page' : undefined}>{crumb.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-xl font-semibold tracking-tight text-slate-900">{title}</h1>
            {meta}
          </div>
          {description && <p className="mt-1 text-[13px] text-slate-500">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}
